import React, { useMemo } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import { useTheme, type Theme } from '@mui/material/styles';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import CloseIcon from '@mui/icons-material/Close';
import type { AppDialogOptions, DialogAnswer, DialogKind } from './types';
import { dismissAnswerForMode, resolveDialogDefaults } from './types';

export interface AppDialogHostProps {
  open: boolean;
  options?: AppDialogOptions;
  onAnswer: (answer: DialogAnswer) => void;
}

function kindIcon(kind: DialogKind) {
  switch (kind) {
    case 'Information':
      return InfoOutlinedIcon;
    case 'Warning':
      return WarningAmberOutlinedIcon;
    case 'Error':
      return ErrorOutlineIcon;
    case 'Question':
      return HelpOutlineIcon;
    default: {
      const _exhaustive: never = kind;
      return _exhaustive;
    }
  }
}

function kindColor(kind: DialogKind, palette: Theme['palette']): string {
  switch (kind) {
    case 'Information':
      return palette.info.main;
    case 'Warning':
      return palette.warning.main;
    case 'Error':
      return palette.error.main;
    case 'Question':
      return palette.primary.main;
    default: {
      const _exhaustive: never = kind;
      return _exhaustive;
    }
  }
}

export function AppDialogHost({ open, options, onAnswer }: AppDialogHostProps) {
  const theme = useTheme();
  const resolved = useMemo(
    () => (options ? resolveDialogDefaults(options) : null),
    [options],
  );

  if (!resolved) {
    return null;
  }

  const { text, kind, mode } = resolved;
  const Icon = kindIcon(kind);
  const iconColor = kindColor(kind, theme.palette);

  const dismiss = () => onAnswer(dismissAnswerForMode(mode));

  const primaryButtonSx = {
    ...(kind === 'Error' && {
      color: 'error.contrastText',
      bgcolor: 'error.main',
      '&:hover': { bgcolor: 'error.dark' },
    }),
    ...(kind === 'Warning' && {
      color: 'warning.contrastText',
      bgcolor: 'warning.main',
      '&:hover': { bgcolor: 'warning.dark' },
    }),
  };

  return (
    <Dialog
      open={open}
      onClose={dismiss}
      aria-labelledby="app-dialog-title"
      aria-describedby="app-dialog-description"
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle
        id="app-dialog-title"
        component="div"
        sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, pr: 1 }}
      >
        <Box
          aria-hidden
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: iconColor,
            mt: 0.25,
          }}
        >
          <Icon fontSize="small" />
        </Box>
        <Typography component="span" sx={{ flex: 1, fontWeight: 600, color: 'text.primary' }}>
          {kind}
        </Typography>
        <IconButton size="small" onClick={dismiss} aria-label="Close" sx={{ mt: -0.5, mr: -0.5 }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent id="app-dialog-description">
        <Typography sx={{ color: 'text.primary', whiteSpace: 'pre-wrap' }}>{text}</Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        {mode === 'OK' && (
          <Button variant="contained" onClick={() => onAnswer('ok')} sx={primaryButtonSx}>
            OK
          </Button>
        )}
        {mode === 'YesNo' && (
          <>
            <Button onClick={() => onAnswer('no')}>No</Button>
            <Button variant="contained" onClick={() => onAnswer('yes')} sx={primaryButtonSx}>
              Yes
            </Button>
          </>
        )}
        {mode === 'YesNoCancel' && (
          <>
            <Button onClick={() => onAnswer('cancel')}>Cancel</Button>
            <Button onClick={() => onAnswer('no')}>No</Button>
            <Button variant="contained" onClick={() => onAnswer('yes')} sx={primaryButtonSx}>
              Yes
            </Button>
          </>
        )}
        {mode === 'OKCancel' && (
          <>
            <Button onClick={() => onAnswer('cancel')}>Cancel</Button>
            <Button variant="contained" onClick={() => onAnswer('ok')} sx={primaryButtonSx}>
              OK
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
